import { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, SkipForward, Sparkles } from "lucide-react";
import demo from "../../data/store_demo_run.json";

const PROMPT_LINE = `Upgrade this weak prompt: "${demo.weak_prompt}" …`;
const DURATION_MS = 20000;

export function StoreDemoPlayer() {
  const [phase, setPhase] = useState("idle");
  const [typed, setTyped] = useState(0);
  const [shown, setShown] = useState(0);
  const raf = useRef(null);
  const startRef = useRef(0);
  const bodyRef = useRef(null);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const finish = () => { setTyped(PROMPT_LINE.length); setShown(demo.output.length); setPhase("done"); };
  const stop = () => { if (raf.current) cancelAnimationFrame(raf.current); };

  const play = () => {
    stop();
    if (reduced) return finish();
    setTyped(0); setShown(0); setPhase("typing");
    startRef.current = performance.now();
    const typeMs = 2200, streamStart = 3000;
    const tick = (now) => {
      const t = now - startRef.current;
      setTyped(Math.min(PROMPT_LINE.length, Math.floor((t / typeMs) * PROMPT_LINE.length)));
      if (t >= streamStart) {
        setPhase("streaming");
        const p = Math.min(1, (t - streamStart) / (DURATION_MS - streamStart));
        setShown(Math.floor(p * demo.output.length));
        if (p >= 1) return setPhase("done");
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };

  useEffect(() => stop, []);
  useEffect(() => { if (bodyRef.current && phase === "streaming") bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [shown, phase]);

  return (
    <section className="mt-16" data-testid="store-demo">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-300">See it run</div>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight">20 seconds. One weak ask → a prompt that works.</h2>
      <p className="mt-3 text-zinc-400 max-w-2xl text-sm">A real, unedited run of <span className="text-white">{demo.prompt_title}</span> inside the vault ({demo.model}, recorded {demo.recorded_at}). Nothing staged — this is what "Run with AI" returned.</p>
      <div className="mt-6 border border-zinc-800 bg-[#070707] shadow-[0_0_60px_-20px_rgba(168,85,247,0.45)]">
        <div className="flex items-center justify-between gap-3 border-b border-zinc-900 px-4 py-2.5">
          <div className="flex items-center gap-2 text-xs text-zinc-500"><span className="w-2.5 h-2.5 rounded-full bg-zinc-700" /><span className="w-2.5 h-2.5 rounded-full bg-zinc-700" /><span className="w-2.5 h-2.5 rounded-full bg-purple-500" /><span className="ml-3 font-mono">stagvault.co.uk · Run with AI</span></div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300/90 border border-amber-400/30 px-2 py-0.5" data-testid="store-demo-label">Recorded output · unedited</span>
        </div>
        <div className="grid md:grid-cols-[1fr_1.4fr]">
          <div className="p-5 border-b md:border-b-0 md:border-r border-zinc-900">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-600">Prompt</div>
            <p className="mt-2 font-mono text-sm text-zinc-200 min-h-[4.5rem]" data-testid="store-demo-prompt">{phase === "idle" ? PROMPT_LINE : PROMPT_LINE.slice(0, typed)}{phase === "typing" && <span className="animate-pulse text-purple-400">▍</span>}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {phase === "idle" || phase === "done" ? (
                <button type="button" onClick={play} className="store-glow inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-black px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold" data-testid="store-demo-play">
                  {phase === "done" ? <RotateCcw size={14} /> : <Play size={14} />} {phase === "done" ? "Replay" : "Play the run"}
                </button>
              ) : (
                <button type="button" onClick={() => { stop(); finish(); }} className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:border-purple-400 px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold" data-testid="store-demo-skip"><SkipForward size={14} /> Skip to result</button>
              )}
            </div>
          </div>
          <div ref={bodyRef} className="p-5 h-72 md:h-80 overflow-y-auto" data-testid="store-demo-output">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-600 flex items-center gap-2"><Sparkles size={11} className="text-purple-400" /> Output</div>
            {phase === "idle" ? (
              <p className="mt-3 text-sm text-zinc-600">Press play to watch the answer stream in exactly as it did.</p>
            ) : (
              <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-200">{demo.output.slice(0, shown)}{phase === "streaming" && <span className="animate-pulse text-purple-400">▍</span>}</pre>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
